import { Button, Card, Heading, Paragraph } from '@digdir/designsystemet-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import styled from '../../../../styled';
import { useGetBuildingAddress } from '../../../../hooks/useGetBuildingAddress';
import { DetailedBuildingDetails } from '../../../../types/building';
import useFormatDate from '../../../../hooks/useFormatDate';
import { Energikarakter } from '../../../../components/energimerke/energikarakter';
import { fromAttestToEnergimerke } from '../../../../components/energimerke/utils';

interface Props {
  building?: DetailedBuildingDetails;
  displayNavigationButton?: boolean;
}

const StyledCard = styled(Card)`
  padding: var(--ds-size-6);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--ds-size-4);
  justify-content: space-between;
`;

const CardHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--ds-size-4);
  min-width: 400px;
  margin-bottom: var(--ds-size-4);
`;

const BuildningNumbers = styled(Paragraph)`
  display: flex;
  flex-direction: row;
  gap: var(--ds-size-4);
`;

export const BuildingCard: FC<Props> = ({ building, displayNavigationButton }) => {
  const { t } = useTranslation();
  const { getBuildingAddress } = useGetBuildingAddress();
  const { formatDate } = useFormatDate();

  const kommunenummer = t('building.identifier_knr_long', {
    value: building?.adresse.kommuneNummer
  });
  const gardsnummer = t('building.identifier_gnr_long', {
    value: building?.gardsNummer
  });
  const bruksnummer = t('building.identifier_bnr_long', {
    value: building?.bruksNummer
  });

  const energiattest = building?.gyldigeEnergiattester[0];
  const energimerke = fromAttestToEnergimerke(energiattest);

  return (
    <StyledCard>
      <div>
        <CardHeaderWrapper>
          <Energikarakter energimerke={energimerke} />
          <div>
            <Heading level={2} data-size="sm">
              {getBuildingAddress(building?.adresse)}
            </Heading>
            <Paragraph>
              {energiattest?.publisertDato &&
                t('energiattestPage.info.dateSigned', { date: formatDate(energiattest?.publisertDato) })}
            </Paragraph>
          </div>
        </CardHeaderWrapper>
        <Paragraph>
          {t('energiattestPage.bruksenhetssnummer', { bruksenhetssnummer: building?.bruksEnhetsNummer })}
        </Paragraph>
        <BuildningNumbers>
          <span>{kommunenummer}</span>
          <span>{gardsnummer}</span>
          <span>{bruksnummer}</span>
        </BuildningNumbers>
        <Paragraph> {t('energiattestPage.bygningsnummer', { bygningsnummer: building?.bygningsNummer })}</Paragraph>
      </div>
      {displayNavigationButton && (
        <div>
          <Button asChild variant="secondary">
            <Link to={`/bygg/${building?.energiObjektId}`}>Vis bolig</Link>
          </Button>
        </div>
      )}
    </StyledCard>
  );
};
